import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useClientId } from '@shared/hooks/useClientId';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import { listFacilities } from '../api/listFacilityApiService';
import type { FacilityListItem } from '../types/facilityListType';
import type { FilterOption } from '@shared/components/FilterPopup';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isValidStatus = (s: string) => ['active', 'inactive'].includes(s);

const sanitizeStatus = (raw: string | null | undefined): string | undefined => {
  if (!raw) {
    return undefined;
  }
  const valid = raw
    .split(',')
    .map((s) => s.trim())
    .filter(isValidStatus);
  return valid.length > 0 ? valid.join(',') : undefined;
};

const statusListFromParam = (raw: string | null | undefined): string[] => {
  if (!raw) {
    return [];
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(isValidStatus);
};

const DEFAULT_STATUS_OPTIONS: FilterOption[] = [
  { label: 'Aktywny', value: 'active', checked: false },
  { label: 'Nieaktywny', value: 'inactive', checked: false },
];

const buildStatusOptions = (
  statusParam: string | undefined
): FilterOption[] => {
  if (!statusParam) {
    return [...DEFAULT_STATUS_OPTIONS];
  }
  const list = statusListFromParam(statusParam);
  return [
    { label: 'Aktywny', value: 'active', checked: list.includes('active') },
    {
      label: 'Nieaktywny',
      value: 'inactive',
      checked: list.includes('inactive'),
    },
  ];
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useFacilityListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);

  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();
  const { clientId, isLoading: clientIdLoading } = useClientId();

  const urlStatus = sanitizeStatus(searchParams.get('status'));
  const urlSearch = searchParams.get('search') ?? '';

  // ── Local state ─────────────────────────────────────────────────────────────
  const [facilities, setFacilities] = useState<FacilityListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [maxFacilityLimit, setMaxFacilityLimit] = useState<number | null>(null);
  const [currentFacilityCount, setCurrentFacilityCount] = useState(0);
  const [isNoFilterFetch, setIsNoFilterFetch] = useState(false);
  const [page, setPage] = useState(1);

  const fetchGenRef = useRef(0);
  const isFirstRender = useRef(true);

  const [search, setSearchRaw] = useState(() =>
    urlSearch.length > 200 ? urlSearch.slice(0, 200) : urlSearch
  );
  const debouncedSearch = useDebounce(search, 500);
  const prevSearchRef = useRef(search);
  const prevDebouncedRef = useRef(debouncedSearch);

  const [statusOptions, setStatusOptions] = useState<FilterOption[]>(() =>
    buildStatusOptions(urlStatus)
  );
  const statusOptionsRef = useRef<FilterOption[]>(
    buildStatusOptions(urlStatus)
  );
  const [appliedFilters, setAppliedFilters] = useState<{
    statusOptions: FilterOption[];
  }>(() => ({ statusOptions: buildStatusOptions(urlStatus) }));

  // ── Sync URL → state (back/forward navigation) ───────────────────────────
  useEffect(() => {
    isSyncingFromUrl.current = true;

    const currentStatus = sanitizeStatus(searchParams.get('status'));
    const currentSearch = (searchParams.get('search') ?? '').slice(0, 200);

    if (currentSearch.trim() !== search.trim()) {
      setSearchRaw(currentSearch);
    }

    const newOpts = buildStatusOptions(currentStatus);
    const serialize = (opts: FilterOption[]) =>
      opts.map((o) => `${o.value}:${String(o.checked)}`).join(',');
    if (serialize(newOpts) !== serialize(appliedFilters.statusOptions)) {
      setStatusOptions(newOpts);
      setAppliedFilters({ statusOptions: newOpts });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [search, appliedFilters]);

  // ── Sync state → URL (search + status only — no page/limit for infinite scroll)
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevSearchRef.current = search;
      return;
    }

    prevSearchRef.current = search;

    const params = new URLSearchParams();
    if (search) {
      params.set('search', search);
    }

    const selectedStatuses = appliedFilters.statusOptions
      .filter((o) => o.checked)
      .map((o) => o.value)
      .join(',');
    if (selectedStatuses) {
      params.set('status', selectedStatuses);
    }

    if (
      params.toString() !==
      new URLSearchParams(searchParams.toString()).toString()
    ) {
      setSearchParams(params, { replace: false });
    }
  }, [search, appliedFilters, setSearchParams, searchParams]);

  // ── Reset list when search or filters change (skip initial mount) ─────────
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(1);
    setFacilities([]);
    setTotalCount(0);
    setIsNoFilterFetch(false);
  }, [debouncedSearch, appliedFilters]);

  // ── statusOptionsRef sync (keeps ref current for handleApplyFilters) ──────
  useEffect(() => {
    statusOptionsRef.current = statusOptions;
  }, [statusOptions]);

  // ── prevDebouncedRef sync ─────────────────────────────────────────────────
  useEffect(() => {
    prevDebouncedRef.current = debouncedSearch;
  }, [debouncedSearch]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const isBootstrapping = privilegesLoading || clientIdLoading;
  const canListFacilities = privileges.canListFacilities;
  // true when the client has hit their subscription facility cap (is_valid=true count >= plan limit).
  // false when no limit is set (maxFacilityLimit === null) or cap not yet reached.
  const isAtFacilityLimit =
    maxFacilityLimit !== null && currentFacilityCount >= maxFacilityLimit;
  // Non-client users (no clientId) can always create; client users need the permission and must not be at the cap.
  // Also disabled while loading — avoids a flash where the button is enabled before the facility count arrives.
  const canCreateFacility =
    isBootstrapping || loading
      ? false
      : !clientId || (privileges.canCreateFacility && !isAtFacilityLimit);

  const fetchFacilities = useCallback(async () => {
    if (isBootstrapping) {
      return;
    }
    if (!canListFacilities || !clientId) {
      setLoading(false);
      return;
    }

    const gen = ++fetchGenRef.current;
    const isFirstPage = page === 1;

    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    const selectedStatuses = appliedFilters.statusOptions
      .filter((o) => o.checked)
      .map((o) => o.value)
      .join(',');

    const trimmedSearch = debouncedSearch.trim();

    const result = await listFacilities(clientId, {
      page,
      limit: PAGE_SIZE,
      facilityIsValid: true,
      ...(trimmedSearch && { search: trimmedSearch }),
      ...(selectedStatuses && { status: selectedStatuses }),
      employeeIsValid: true,
    });

    if (gen !== fetchGenRef.current) {
      return;
    }

    if (result.success) {
      const incoming = result.facilities ?? [];
      if (isFirstPage) {
        setFacilities(incoming);
      } else {
        setFacilities((prev) => [...prev, ...incoming]);
      }
      setTotalCount(result.totalCount ?? 0);
      setMaxFacilityLimit(result.maxFacilityLimit ?? null);
      setCurrentFacilityCount(result.currentFacilityCount ?? 0);
      setIsNoFilterFetch(!trimmedSearch && !selectedStatuses);
    } else {
      setError(result.error ?? 'Nie udało się pobrać placówek.');
      if (isFirstPage) {
        setFacilities([]);
        setTotalCount(0);
      }
    }

    if (isFirstPage) {
      setLoading(false);
    } else {
      setLoadingMore(false);
    }
  }, [
    isBootstrapping,
    canListFacilities,
    clientId,
    page,
    debouncedSearch,
    appliedFilters,
  ]);

  useEffect(() => {
    void fetchFacilities();
  }, [fetchFacilities]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSearchChange = useCallback(
    (newSearch: string) => {
      if (newSearch.length > 200) {
        return;
      }
      if (!isSyncingFromUrl.current && newSearch.trim() !== search.trim()) {
        setLoading(true);
      }
      setSearchRaw(newSearch);
    },
    [search]
  );

  const handleSetStatusOptions = useCallback((opts: FilterOption[]) => {
    statusOptionsRef.current = opts;
    setStatusOptions(opts);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({ statusOptions: statusOptionsRef.current });
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatusOptions([...DEFAULT_STATUS_OPTIONS]);
    setAppliedFilters({ statusOptions: [...DEFAULT_STATUS_OPTIONS] });
  }, []);

  const loadMore = useCallback(() => {
    if (!loading && !loadingMore) {
      setPage((prev) => prev + 1);
    }
  }, [loading, loadingMore]);

  // ── Access check ─────────────────────────────────────────────────────────
  const isAccessDenied = !isBootstrapping && (!canListFacilities || !clientId);

  // ── Derived ───────────────────────────────────────────────────────────────
  const isSearchClearing =
    !search.trim() &&
    debouncedSearch.trim() !== '' &&
    !appliedFilters.statusOptions.some((o) => o.checked);

  const justClearedSearch =
    prevDebouncedRef.current.trim() !== '' &&
    debouncedSearch.trim() === '' &&
    !appliedFilters.statusOptions.some((o) => o.checked);

  const hasNextPage = facilities.length < totalCount;

  return {
    facilities,
    loading:
      isBootstrapping || loading || search.trim() !== debouncedSearch.trim(),
    loadingMore,
    error,
    totalCount,
    isNoFilterFetch,
    search,
    setSearch: handleSearchChange,
    isSearchClearing: isSearchClearing || justClearedSearch,
    statusOptions,
    setStatusOptions: handleSetStatusOptions,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    hasNextPage,
    loadMore,
    isAccessDenied,
    canListFacilities,
    canCreateFacility,
    hasCreateFacilityPermission: privileges.canCreateFacility,
    isAtFacilityLimit,
    maxFacilityLimit,
    refetch: fetchFacilities,
  };
};
